// What a manager may see and do. The owner can do everything; mechanics never see money.
// Each manager has their own switches (Staff → edit the person); blank = the default below.
export const PERMS = [
  { key: 'purchasing', label: 'Order & receive parts, scan vendor invoices, see vendor bills', def: true },
  { key: 'costs', label: 'See part costs and profit', def: false },
  { key: 'pay_vendors', label: 'Mark vendor bills paid', def: false },
  { key: 'void', label: 'Void invoices and remove payments', def: false },
  { key: 'money', label: 'Money & Expenses page (profit, bills, expenses)', def: false },
  { key: 'reports', label: 'Reports and team performance', def: false },
  { key: 'advised_photos', label: 'Show / hide / delete photos on "advised — customer declined" items', def: false },
] as const;
export type PermKey = (typeof PERMS)[number]['key'];
export const PRESETS: Record<string, { label: string; perms: Record<PermKey, boolean> }> = {
  writer: { label: 'Service writer (invoices only)', perms: { purchasing: false, costs: false, pay_vendors: false, void: false, money: false, reports: false, advised_photos: true } },
  parts: { label: 'Parts person', perms: { purchasing: true, costs: true, pay_vendors: false, void: false, money: false, reports: false, advised_photos: false } },
  office: { label: 'Office manager (almost everything)', perms: { purchasing: true, costs: true, pay_vendors: true, void: true, money: true, reports: true, advised_photos: true } },
};

export function parsePerms(raw: unknown): Record<PermKey, boolean> {
  let o: Record<string, unknown> = {};
  try { o = typeof raw === 'string' && raw ? JSON.parse(raw) : (raw as Record<string, unknown>) || {}; } catch { o = {}; }
  return Object.fromEntries(PERMS.map(p => [p.key, typeof o[p.key] === 'boolean' ? o[p.key] : p.def])) as Record<PermKey, boolean>;
}

/** Can this person do it? Owner always, mechanic never, manager by their switches. */
export function can(user: { role: string; perms?: Partial<Record<PermKey, boolean>> } | null | undefined, k: PermKey) {
  if (!user) return false;
  if (user.role === 'owner') return true;
  if (user.role !== 'manager') return false;
  return user.perms?.[k] ?? PERMS.find(p => p.key === k)!.def;
}

// ───────── Shop floor (phone / tablet) roles ─────────
// What someone in a shop-floor role may do. Never money: costs, prices and pay are never on the floor app.
export const FLOOR_PERMS = [
  { key: 'see_all', group: 'Jobs', label: 'Works on every truck in the shop', hint: 'Off: only the jobs given to them', def: true },
  { key: 'view_all', group: 'Jobs', label: 'Sees the whole shop', hint: 'Shop board: every truck and job — opens a truck to take over a job or put a part on it', def: true },
  { key: 'add_jobs', group: 'Jobs', label: 'Add jobs on a truck', hint: '"+ Add job" on the truck', def: true },
  { key: 'assign', group: 'Jobs', label: 'Give jobs to mechanics', hint: 'Pick who does each job', def: false },
  { key: 'take_over', group: 'Jobs', label: "Take over another mechanic's job", hint: 'Their time so far stays theirs; the office is told', def: true },
  { key: 'check_in', group: 'Trucks & customers', label: 'Check trucks in', hint: 'New work order · scan VIN · add a truck / trailer · add a new company', def: true },
  { key: 'parts_search', group: 'Parts', label: 'Search the parts list', hint: 'Name, bin, how many on the shelf — never prices', def: true },
  { key: 'parts_use', group: 'Parts', label: 'Record parts used on a job', def: true },
  { key: 'parts_new', group: 'Parts', label: 'Part not in the list (photo + name)', def: true },
  { key: 'runs', group: 'Other', label: 'Parts runs (go get a part)', def: true },
  { key: 'receipts', group: 'Other', label: 'Receipts (bought something for the shop)', def: true },
] as const;
export type FloorKey = (typeof FLOOR_PERMS)[number]['key'];

export function parseFloor(raw: unknown): Record<FloorKey, boolean> {
  let o: Record<string, unknown> = {};
  try { o = typeof raw === 'string' && raw ? JSON.parse(raw) : (raw as Record<string, unknown>) || {}; } catch { o = {}; }
  return Object.fromEntries(FLOOR_PERMS.map(p => [p.key, typeof o[p.key] === 'boolean' ? o[p.key] : p.def])) as Record<FloorKey, boolean>;
}

/** May this person do it on the shop floor? Owner / office always; a mechanic by their role's switches. */
export function floorCan(user: { role: string; floor?: Partial<Record<FloorKey, boolean>> } | null | undefined, k: FloorKey) {
  if (!user) return false;
  if (user.role !== 'mechanic') return true;
  return user.floor?.[k] ?? FLOOR_PERMS.find(p => p.key === k)!.def;
}
